import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { ASC } from "const"
import {
    StoreType,
    MarkovExpressionParseElements,
    MarkovExpressionAllowedCharacters,
    MarkovExpressionTargetIsWord,
    SortMarkovVariables,
} from "client/store"
import { Empty } from "client/components"
import {
    ExpressionEditorWrapper,
    ExpressionEditorFormatted,
    VariableDropdownList,
    VariableDropdownItem,
} from "./styled"

const LEFT = "ArrowLeft"
const RIGHT = "ArrowRight"
const UP = "ArrowUp"
const DOWN = "ArrowDown"

let caretPos: number = -1
let targetNode: HTMLElement = null

interface ExpressionEditorProps {
    value: string
    onChange: (value: string) => void
}

const ExpressionEditor = ({
    value,
    onChange,
}: ExpressionEditorProps) => {
    const variableList = useSelector((state: StoreType) => state.markovVariable.variableList)
    const variableListByName = useSelector((state: StoreType) => state.markovVariable.variableListByName)

    const [variableIds, setVariableIds] = useState<number[]>([])
    const [search, setSearch] = useState<string>(null)
    const [showList, setShowList] = useState(false)

    const hideList = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        if (!target.closest(ExpressionEditorWrapper)) {
            setShowList(false)
        }
    }

    useEffect(() => {
        document.addEventListener("click", hideList)
        return () => document.removeEventListener("click", hideList)
    }, [])

    useEffect(() => {
        const parsedExp = MarkovExpressionParseElements(value)
        let formatted = ""

        if (parsedExp && parsedExp.length) {
            parsedExp.forEach((parsed) => {
                if (variableListByName[parsed]) formatted += `<span class="variable">${parsed}</span>`
                else formatted += `<span>${parsed}</span>`
            })
        }

        const editor = document.body.querySelector(ExpressionEditorFormatted) as HTMLElement
        editor.innerHTML = formatted
        setCaretPos(editor)
    }, [value])

    useEffect(() => {
        const sortedIds = SortMarkovVariables(Object.values(variableList), "order", ASC, { search })
        setVariableIds(sortedIds)
    }, [search])

    const onChangeCaretPosition = () => {
        const selection = window.getSelection()
        if (
            selection.anchorNode // text node
            && selection.anchorNode.parentNode // span node
            && selection.anchorNode.textContent
        ) {
            targetNode = selection.anchorNode.parentNode as HTMLElement
            setSearch(MarkovExpressionTargetIsWord(selection.anchorNode.textContent) ? selection.anchorNode.textContent : null)
            setShowList(true)
        } else {
            const editor = document.body.querySelector(ExpressionEditorFormatted) as HTMLElement
            targetNode = editor
            setSearch(null)
            setShowList(true)
        }
    }

    const getCaretPos = () => {
        const selection = window.getSelection()
        if (
            selection.anchorNode // text node
            && selection.anchorNode.parentNode // span node
            && selection.anchorNode.textContent
        ) {
            caretPos = selection.focusOffset
            const parent = selection.anchorNode.parentNode // span node
            let prevChild = parent.previousSibling
            while (prevChild) {
                caretPos += prevChild.textContent.length
                prevChild = prevChild.previousSibling
            }
        } else {
            caretPos = 0
        }
    }

    const setCaretPos = (editor: HTMLElement) => {
        if (caretPos < 0) return // don't focus on init

        let nextChild = editor.firstChild // span node
        while (nextChild && caretPos > nextChild.textContent.length) {
            caretPos -= nextChild.textContent.length
            nextChild = nextChild.nextSibling
        }

        if (
            nextChild
            && nextChild.firstChild // text node
            && nextChild.firstChild.textContent
            && caretPos <= nextChild.firstChild.textContent.length
        ) {
            const node = nextChild.firstChild // text node
            const selection = window.getSelection()
            const range = document.createRange()
            range.setStart(node, caretPos)
            range.collapse(true)
            selection.removeAllRanges()
            selection.addRange(range)

            // search
            targetNode = nextChild as HTMLElement // span node
            setSearch(MarkovExpressionTargetIsWord(node.textContent) ? node.textContent : null)
        } else {
            targetNode = editor
            setSearch(null)
        }
    }

    const insertVariable = (varId: number) => {
        const variable = variableList[varId]
        if (variable && targetNode) {
            if (MarkovExpressionTargetIsWord(targetNode.innerText)) {
                targetNode.innerText = variable.name // if variable - replace text
            } else {
                targetNode.innerText += variable.name // else - add text
            }

            caretPos = 0
            let prevNode = targetNode
            while (prevNode) {
                caretPos += prevNode.textContent.length
                prevNode = prevNode.previousSibling as HTMLElement
            }

            const editor = document.body.querySelector(ExpressionEditorFormatted) as HTMLElement
            if (editor.innerText === value) {
                setCaretPos(editor) // force set caret, onChange event will not fire
            } else {
                onChange(editor.innerText)
            }
        }
    }

    return (
        <ExpressionEditorWrapper>
            <ExpressionEditorFormatted
                contentEditable
                onKeyDown={(e) => {
                    // allowed characters
                    if (!MarkovExpressionAllowedCharacters(e.key)) e.preventDefault()
                    if (e.key === "Enter") e.preventDefault()
                }}
                onInput={(e) => {
                    getCaretPos()
                    onChange(e.currentTarget.innerText)
                }}
                onKeyUp={(e) => {
                    // search by focused element
                    if (e.key === LEFT || e.key === RIGHT || e.key === UP || e.key === DOWN) {
                        onChangeCaretPosition()
                    }
                }}
                onClick={() => {
                    // search by focused element
                    onChangeCaretPosition()
                }}
            />

            <VariableDropdownList show={showList}>
                {
                    variableIds.map((varId) => (
                        <VariableDropdownItem
                            key={varId}
                            onClick={() => insertVariable(varId)}
                        >
                            {variableList[varId].name}
                        </VariableDropdownItem>
                    ))
                }
                {
                    !variableIds.length && (
                        <VariableDropdownItem>
                            <Empty />
                        </VariableDropdownItem>
                    )
                }
            </VariableDropdownList>
        </ExpressionEditorWrapper>
    )
}
export default ExpressionEditor
